import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { APIClient, HandleError } from "@/api/axiosClient";
import { toast } from "@/components/ui/use-toast";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// Define a type for the form fields
interface GiftCardFormFields {
  brandId?: string;
  redemptionMerchantId?: string;
  productCode?: string;
  productName?: string;
  giftCardType?: string;
  validityInMonths?: string;
  minValue?: string;
  maxValue?: string;
  denominationIncrement?: string;
  currencyId?: string;
  loyyalCommissionPercentage?: string;
  fulfilmentType?: string;
}

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

const GiftCardProductCreatePage = () => {
  const navigate = useNavigate();
  const [formD, setFormD] = useState<GiftCardFormFields>({});
  const [files, setFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [productDesc, setProductDesc] = useState("");
  const [howToUse, setHowToUse] = useState("");
  const [htmlText, setHtmlText] = useState("");
  const [categorySelected, setCategorySelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormD((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    // Clean up previous previews
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    // Generate previews
    const previews = selectedFiles.map((file) =>
      file instanceof File ? URL.createObjectURL(file) : ""
    );
    setImagePreviews(previews);
  };

  useEffect(() => {
    async function fetchBrands() {
      setBrandsLoading(true);
      try {
        const { data } = await BrandApi.fetchAllBrands();
        setBrands(data || []);
      } catch (err) {
        setBrands([]);
        toast({ title: "Failed to fetch brands", variant: "destructive" });
      } finally {
        setBrandsLoading(false);
      }
    }
    fetchBrands();
  }, []);

  useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(data?.redemptionMerchantVOS || []);
      } catch (err) {
        setMerchants([]);
        toast({ title: "Failed to fetch merchants", variant: "destructive" });
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);
    } catch (err) {
      setCategories([]);
      toast({ title: "Failed to fetch categories", variant: "destructive" });
    } finally {
      setCategoriesLoading(false);
    }
  };

  const renderCategories = (categories) => (
    <div className="flex flex-col gap-2">
      {categories.map((cat) => {
        const isExpanded = expandedCategoryIds.includes(cat.id);
        const hasSub = cat.subCategoryVOs && cat.subCategoryVOs.length > 0;
        return (
          <div key={cat.id} className="ml-2">
            <div
              className="flex items-center gap-2 border rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-50"
              onClick={() => {
                if (hasSub) {
                  setExpandedCategoryIds((ids) =>
                    isExpanded
                      ? ids.filter((id) => id !== cat.id)
                      : [...ids, cat.id]
                  );
                } else {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }
              }}
            >
              <span className="flex-1">{cat.categoryName}</span>
              {hasSub && <span>{isExpanded ? "▼" : "▶"}</span>}
              <input
                type="radio"
                checked={selectedCategory?.id === cat.id}
                onChange={() => {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
                readOnly
              />
            </div>
            {hasSub && isExpanded && renderCategories(cat.subCategoryVOs)}
          </div>
        );
      })}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let updatedFormData = {};
    try {
      updatedFormData = {
        brandVO: { id: formD?.brandId },
        redemptionMerchantVO: { id: formD?.redemptionMerchantId },
        productCode: formD?.productCode,
        productName: formD?.productName,
        productDescription: productDesc,
        giftCardType: formD?.giftCardType,
        validityInMonths: formD?.validityInMonths,
        termsAndConditions: htmlText,
        howToUse: howToUse,
        minValue: formD?.minValue,
        maxValue: formD?.maxValue,
        denominationIncrement: formD?.denominationIncrement,
        productType: "GIFT_CARD",
        currencyVO: selectedCurrency ? { id: selectedCurrency.id } : undefined,
        loyyalCommissionPercentage: formD?.loyyalCommissionPercentage,
        fulfilmentType: formD?.fulfilmentType,
        platformCategoryIds: selectedCategory ? [selectedCategory.id] : [],
      };

      const formData = new FormData();
      if (files?.length > 0) {
        files.forEach((file) => {
          formData.append("giftCardProductLogo", file);
        });
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);

      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });
      toast({ title: "Gift card added successfully", variant: "default" });
      // Navigate back to gift cards products screen after successful creation
      navigate("/rewardpoint/products");
    } catch (error) {
      toast({ title: HandleError(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  return (
    <div className="space-y-6 w-full py-8 px-8">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/products">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create Gift Card Product
          </h1>
          <p className="text-gray-500">
            Add a new gift card product to your catalog
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo */}
                  <div>
                    <label className="block font-medium mb-2">Logo</label>
                    <div className="w-28 h-28 rounded-lg bg-gray-100 flex items-center justify-center mb-2 relative">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button variant="ghost" size="icon" asChild>
                          <span>
                            <Plus className="h-8 w-8 text-gray-400" />
                          </span>
                        </Button>
                      </label>
                      {imagePreviews.length > 0 && (
                        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-1">
                          {imagePreviews.map((src, idx) => (
                            <img
                              key={idx}
                              src={src}
                              alt={`Preview ${idx + 1}`}
                              className="w-24 h-24 object-contain rounded"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Brand & Merchant */}
                  <div>
                    <label className="block font-medium mb-1">Brand*</label>
                    <Select
                      name="brandId"
                      onValueChange={(v) =>
                        setFormD((prev) => ({ ...prev, brandId: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : brands.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No brands
                          </div>
                        ) : (
                          brands.map((b) => (
                            <SelectItem key={b.id} value={b.id + ""}>
                              {b.brandName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Merchant*</label>
                    <Select
                      name="redemptionMerchantId"
                      onValueChange={(v) =>
                        setFormD((prev) => ({
                          ...prev,
                          redemptionMerchantId: v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Merchant" />
                      </SelectTrigger>
                      <SelectContent>
                        {merchantsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : merchants.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No merchants
                          </div>
                        ) : (
                          merchants.map((m) => (
                            <SelectItem key={m.id} value={m.id + ""}>
                              {m.merchantName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Product Name*
                    </label>
                    <Input
                      name="productName"
                      placeholder="Enter product name"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Product Code*
                    </label>
                    <Input
                      name="productCode"
                      placeholder="Enter product code.."
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-medium mb-1">
                      Product Description
                    </label>
                    <Textarea
                      placeholder="Enter product description"
                      rows={4}
                      onChange={(e) => setProductDesc(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Gift Card Details */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Gift Card Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-medium mb-1">
                      Validity* <span className="text-xs">(Months)</span>
                    </label>
                    <Input
                      name="validityInMonths"
                      placeholder="Validity in months"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Type*</label>
                    <Select
                      name="giftCardType"
                      onValueChange={(v) =>
                        setFormD((prev) => ({ ...prev, giftCardType: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="instore">In-store</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Gift Code Fulfillment*
                    </label>
                    <Select
                      name="fulfilmentType"
                      onValueChange={(v) =>
                        setFormD((prev) => ({ ...prev, fulfilmentType: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Fulfillment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERCHANT_API">
                          MERCHANT_API
                        </SelectItem>
                        <SelectItem value="MERCHANT_EMAIL">
                          MERCHANT_EMAIL
                        </SelectItem>
                        <SelectItem value="LOYYAL_GENERATED">
                          LOYYAL_GENERATED
                        </SelectItem>
                        <SelectItem value="MERCHANT_UPLOADED">
                          MERCHANT_UPLOADED
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Platform Category
                    </label>
                    <Dialog
                      open={categoryDialogOpen}
                      onOpenChange={setCategoryDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full text-left"
                          onClick={fetchCategories}
                        >
                          {selectedCategory
                            ? selectedCategory.categoryName
                            : "Select"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md w-full p-6">
                        <div className="rounded-xl border p-6 bg-white">
                          {categoriesLoading ? (
                            <div className="text-center text-gray-500 py-8">
                              Loading...
                            </div>
                          ) : (
                            renderCategories(categories)
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-8">
            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block font-medium mb-1">
                      Denomination
                    </label>
                    <div className="flex gap-2">
                      <Input
                        name="minValue"
                        placeholder="Min value"
                        className="flex-1"
                        onChange={handleInputChange}
                      />
                      <Input
                        name="maxValue"
                        placeholder="Max value"
                        className="flex-1"
                        onChange={handleInputChange}
                      />
                      <Input
                        name="denominationIncrement"
                        placeholder="Increment"
                        className="flex-1"
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Currency*</label>
                    <Select
                      value={selectedCurrency?.id || ""}
                      onValueChange={(v) =>
                        setSelectedCurrency(
                          CURRENCY_OPTIONS.find((c) => c.id === Number(v))
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id.toString()}>
                            {opt.currencyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Loyyal Commission* (%)
                    </label>
                    <Input
                      name="loyyalCommissionPercentage"
                      placeholder="Commission"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Terms & Conditions
                    </label>
                    <Textarea
                      placeholder="Enter terms & conditions"
                      rows={4}
                      onChange={(e) => setHtmlText(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* How To Use */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  How To Use
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <Textarea
                  placeholder="Enter how to use"
                  rows={6}
                  onChange={(e) => setHowToUse(e.target.value)}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        <Separator className="my-8" />
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Create Product"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default GiftCardProductCreatePage;
